'use client';

import { useState } from 'react';
import { Locale, countryLanguages, defaultLanguage } from 'shared/src/types/languages.type';
import { processContact } from '@/server/send-email';
import type { PartnerFormContent } from '@/lib/ui-content/partner-form-content';
import {
  ExploreFormError,
  ExploreFormSection,
  ExploreFormSuccess,
  ExploreSubmit,
  RequiredMark,
  exploreFieldClass,
  exploreLabelClass,
} from '@/components/explore/explore-form-fields';

type Props = {
  lang: string;
  content: PartnerFormContent;
};

const inputClass = exploreFieldClass;
const labelClass = exploreLabelClass;

const initialFormData = {
  fname: '',
  email: '',
  phone: '',
  company: '',
  website: '',
  role: '',
  catalogSize: '',
  message: '',
  hp: '',
};

/** Structural twin of ContactForm, submitting through the same processContact action tagged formType: 'partner'. */
export function PartnerForm({ lang, content: t }: Props) {
  const locale = countryLanguages[lang as Locale] ?? defaultLanguage;
  const [formData, setFormData] = useState(initialFormData);
  const [partnershipTypes, setPartnershipTypes] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const togglePartnershipType = (value: string) => {
    setPartnershipTypes((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const partnershipTypeLabels = t.partnershipType.options
        .filter((opt) => partnershipTypes.includes(opt.value))
        .map((opt) => opt.label)
        .join(', ');

      const catalogSizeLabel =
        t.catalogSize.options.find((opt) => opt.value === formData.catalogSize)?.label ?? formData.catalogSize;

      const result = await processContact({
        name: formData.fname,
        fname: formData.fname,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        website: formData.website,
        role: formData.role,
        catalogSize: catalogSizeLabel,
        partnershipType: partnershipTypeLabels,
        message: formData.message,
        hp: formData.hp,
        locale: locale.path,
        formType: 'partner',
      });

      if (result && result.ok === false) {
        setStatus('error');
        return;
      }

      setStatus('success');
      setFormData(initialFormData);
      setPartnershipTypes([]);
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return <ExploreFormSuccess title={t.successTitle} body={t.successMessage} />;
  }

  return (
    <form className="space-y-8" onSubmit={handleSubmit} data-partner-form>
      {/* Honeypot: hidden from humans, bots tend to fill it. */}
      <input
        type="text"
        name="hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={formData.hp}
        onChange={(e) => setFormData((prev) => ({ ...prev, hp: e.target.value }))}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <ExploreFormSection title={t.sections.details.title}>
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
            <div>
              <label htmlFor="partner-fname" className={labelClass}>
                <RequiredMark />
                {t.fname.label}
              </label>
              <input
                type="text"
                id="partner-fname"
                required
                value={formData.fname}
                onChange={(e) => setFormData((prev) => ({ ...prev, fname: e.target.value }))}
                className={inputClass}
                placeholder={t.fname.placeholder}
              />
            </div>

            <div>
              <label htmlFor="partner-email" className={labelClass}>
                <RequiredMark />
                {t.email.label}
              </label>
              <input
                type="email"
                id="partner-email"
                required
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                className={inputClass}
                placeholder={t.email.placeholder}
              />
            </div>

            <div>
              <label htmlFor="partner-phone" className={labelClass}>
                {t.phone.label}
              </label>
              <input
                type="tel"
                id="partner-phone"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                className={inputClass}
                placeholder={t.phone.placeholder}
              />
            </div>

            <div>
              <label htmlFor="partner-company" className={labelClass}>
                {t.company.label}
              </label>
              <input
                type="text"
                id="partner-company"
                value={formData.company}
                onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                className={inputClass}
                placeholder={t.company.placeholder}
              />
            </div>

            <div>
              <label htmlFor="partner-website" className={labelClass}>
                {t.website.label}
              </label>
              <input
                type="text"
                id="partner-website"
                inputMode="url"
                value={formData.website}
                onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                className={inputClass}
                placeholder={t.website.placeholder}
              />
            </div>

            <div>
              <label htmlFor="partner-role" className={labelClass}>
                {t.role.label}
              </label>
              <input
                type="text"
                id="partner-role"
                value={formData.role}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                className={inputClass}
                placeholder={t.role.placeholder}
              />
            </div>
          </div>
        </div>
      </ExploreFormSection>

      <ExploreFormSection title={t.sections.interest.title}>
        <div className="space-y-8">
          <div>
            <p className={labelClass}>{t.partnershipType.label}</p>
            <div className="flex flex-wrap gap-2">
              {t.partnershipType.options.map((option) => {
                const selected = partnershipTypes.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`inline-flex cursor-pointer items-center rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-200 ${
                      selected
                        ? 'border-ink bg-ink text-card'
                        : 'border-hairline text-muted hover:border-accent hover:text-ink'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={selected}
                      onChange={() => togglePartnershipType(option.value)}
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="partner-catalog-size" className={labelClass}>
              {t.catalogSize.label}
            </label>
            <select
              id="partner-catalog-size"
              value={formData.catalogSize}
              onChange={(e) => setFormData((prev) => ({ ...prev, catalogSize: e.target.value }))}
              className={inputClass}
            >
              <option value="">{t.catalogSize.placeholder}</option>
              {t.catalogSize.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </ExploreFormSection>

      <ExploreFormSection title={t.sections.message.title}>
        <div className="space-y-8">
          <label htmlFor="partner-message" className={labelClass}>
            <RequiredMark />
            {t.message.label}
          </label>
          <textarea
            id="partner-message"
            required
            rows={5}
            value={formData.message}
            onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
            className={`${inputClass} resize-none`}
            placeholder={t.message.placeholder}
          />
        </div>
      </ExploreFormSection>

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <ExploreSubmit label={t.submit} loadingLabel={t.sending} loading={status === 'loading'} />
        {status === 'error' && <ExploreFormError message={t.errorMessage} />}
      </div>
    </form>
  );
}
